import { Injectable, signal, WritableSignal } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  EMPTY,
  map,
  Observable,
  of,
  take,
  tap,
} from 'rxjs';
import { ISearchHistory } from '../models/search-history.interface';
import { DashboardService } from './dashboard.service';
import { Property, PropertyResponse } from '../models/property.model';
import { FilterForm } from '../models/propertyFilter.interface';
import { ProvincesResponse } from '../models/category';
import { AuthService } from './auth.service';
import { GlobalStateService } from './global-state.servece';

@Injectable({
  providedIn: 'root',
})
export class SearchStateService {
  public readonly searchQuery: WritableSignal<string> = signal('');

  private readonly searchHistory$: BehaviorSubject<ISearchHistory[] | null> =
    new BehaviorSubject<ISearchHistory[] | null>(null);

  private readonly params$: BehaviorSubject<FilterForm> =
    new BehaviorSubject<FilterForm>({});

  constructor(
    private readonly dashboardService: DashboardService, 
    private readonly authService: AuthService,
    private readonly globalStateService: GlobalStateService,
  ) {}

  setFilterParams(params: FilterForm): void {
    const newParams = Object.keys(params).length
      ? {
          ...this.params$.value,
          ...params,
          province: params.province?.length
            ? params.province
            : this.globalStateService.provincesID
        }
      : {};
    this.params$.next(newParams);
  }

  defaultParams(): Observable<string[]> {
    this.globalStateService.provincesID
    return this.dashboardService.getProvinces().pipe(
      take(1),
      map((result: ProvincesResponse) =>
        result.data.provinces.map(item => item._id)
      ),
      tap((result: string[]) => this.setFilterParams({ province: result }))
    );
  }

  getSearchHistory(): Observable<ISearchHistory[]> {
    return this.searchHistory$.pipe(map(value => (value ?? []).slice(0, 4)));
  }

  setSearchQuery(value: string): void {
    this.searchQuery.set(value);
  }

  fetchSearchHistory(): Observable<{
    searchTexts: ISearchHistory[];
  }> {
    return this.authService.isGuest ? of({ searchTexts: [] }) : this.dashboardService.getSearchHistory().pipe(
      take(1),
      tap(data => this.searchHistory$.next(data.searchTexts || [])),
      catchError(() => {
        return EMPTY;
      })
    );
  }

  getSearchData(properties: Property[]): Property[] {
    const uniqElements = new Map<string, Property>();

    properties.forEach(prop => {
      uniqElements.set(prop._id, prop);
    });

    return [...uniqElements.values()];
  }

  getFilterData(): Observable<PropertyResponse> {
    return this.dashboardService.getSearchedFilteredData(
      this.searchQuery(),
      this.params$.value,
    );
  }
}
